import React, { useState, useContext, useEffect, useMemo } from 'react';
import { ToolbarProps, View } from 'react-big-calendar';
import {
  Button,
  Typography,
  Toolbar,
  InputLabel,
  Select,
  MenuItem,
  makeStyles,
  FormControl,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Checkbox,
  ListItemText,
  Collapse,
  Popover,
  List,
  ListItem,
  Tabs,
  Tab,
  Badge,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import FilterListIcon from '@material-ui/icons/FilterList';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useForm } from 'react-hook-form';
import CalendarContext from './CalendarContext';
import AuthContainer from 'components/AuthContainer/AuthContainer';
import CalendarMultiUserAutocomplete from './CalendarMultiUserAutocomplete';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarAPI, UserAPI, BranchAPI } from 'api';
import { useSnackbar } from 'notistack';
import { userHasAccess } from 'models/Role';
import UserContext from 'state/UserContext';
import { Role } from 'models/Role';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addMonths,
} from 'date-fns';
import { getCalendarUserList } from 'api/calendarApi';

const useStyles = makeStyles((theme) => ({
  topToolbar: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  toolbar: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
  },
  formControl: {
    minWidth: 120,
    margin: theme.spacing(1),
  },
  todayBtn: {
    marginRight: theme.spacing(2),
  },
  label: {
    padding: theme.spacing(1),
  },
  navigationContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  userSelectContainer: {
    flex: 1,
    position: 'relative',
  },
  filterIconButton: {
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    transition: 'right 0.2s ease',
  },
  filterIconButtonWithUsers: {
    right: 1,
  },
  filterBadge: {
    position: 'absolute',
    right: 40,
    top: '50%',
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      fontSize: '0.65rem',
      height: '18px',
      minWidth: '18px',
    },
  },
  filterBadgeWithUsers: {
    right: 70,
  },
  buttonContainer: {
    marginLeft: 10,
  },
  filtersContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
    },
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  popover: {
    marginTop: theme.spacing(1),
    '& .MuiPaper-root': {
      boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)',
    },
  },
  popoverContent: {
    width: 350,
    maxHeight: 500,
    overflow: 'auto',
  },
  singleTabPopover: {
    marginTop: theme.spacing(1),
    '& .MuiPaper-root': {
      boxShadow: '0px 5px 15px rgba(0,0,0,0.2)',
      minWidth: 200,
      maxWidth: 250,
    },
  },
  singleTabBox: {
    padding: theme.spacing(1),
    background: theme.palette.background.paper,
  },
  noIndicator: {
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
  stateHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '1px 2px',
    marginTop: theme.spacing(0.1),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  stateText: {
    fontWeight: 'bold',
  },
  branchItem: {
    paddingLeft: theme.spacing(2),
    padding: '1px 4px 1px 12px',
    margin: 0,
  },
  roleItem: {
    padding: '1px 4px',
    margin: 0,
  },
  blueCheckbox: {
    color: theme.palette.primary.main,
    '&.Mui-checked': {
      color: theme.palette.primary.main,
    },
  },
  tabPanel: {
    padding: theme.spacing(1),
  },
  datePickerInput: {
    display: 'flex',
    zIndex: 100,
    alignItems: 'center',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(16),
    letterSpacing: 0,
    padding: 0,
    border: 'none',
    cursor: 'pointer',
    minWidth: '260px',
    '& input': {
      padding: 0,
      minWidth: 0,
      textAlign: 'center',
      cursor: 'default',
    },

    '& svg': {
      left: 10,
      verticalAlign: 'middle',
      textAlign: 'right',
      height: 23,
      width: 23,
    },
  },
  pickerPaper: {
    boxShadow: '0px 4px 16px rgba(0,0,0,0.15) !important', // subtle shadow
    borderRadius: 8,
  },
}));

interface StateFilter {
  state: string;
  branches: string[];
  expanded: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  const classes = useStyles();

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`filter-tabpanel-${index}`}
      aria-labelledby={`filter-tab-${index}`}
      {...other}>
      {value === index && <Box className={classes.tabPanel}>{children}</Box>}
    </div>
  );
}

const USER_ROLES = [
  { value: 'Administrator', label: 'Administrator' },
  { value: 'ProjectManager', label: 'Project Coordinator' },
  { value: 'Supervisor', label: 'Supervisor' },
  { value: 'Technician', label: 'Technician' },
];

const CalendarToolbar = (props: ToolbarProps) => {
  const { user } = useContext(UserContext);
  const useAccess = userHasAccess(user, [
    'Administrator',
    'ProjectManager',
    'Supervisor',
  ]);

  const { data: allBranchesData } = useQuery(
    ['list-branches'],
    BranchAPI.getBranches,
    { staleTime: 30 * 60 * 1000, enabled: useAccess }
  );
  const { data: pinnedUsers } = useQuery(
    ['list-pinned-calendar-users'],
    CalendarAPI.listPinnedCalendarUsers,
    { staleTime: 30 * 60 * 1000, enabled: useAccess }
  );
  const { onNavigate, onView, date } = props;
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [view, setView] = useState<View>(props.view);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const calendarCtx = useContext(CalendarContext);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [stateFilters, setStateFilters] = useState<StateFilter[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date>(date || new Date());

  const {
    control,
    watch,
    formState: { isDirty },
    reset,
  } = useForm();
  const watchSelectedUsers = watch('selectedUsers');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setSelectedUsers(pinnedUsers);
  }, [pinnedUsers]);

  useEffect(() => {
    if (isDirty) {
      calendarCtx.setSelectedUsers(watchSelectedUsers);
    } else {
      calendarCtx.setSelectedUsers(pinnedUsers);
    }
  }, [calendarCtx, pinnedUsers, watchSelectedUsers, isDirty]);

  useEffect(() => {
    setSelectedDate(date || new Date());
  }, [date]);

  const queryClient = useQueryClient();

  const { mutate: addOrUpdateMutation } = useMutation(
    CalendarAPI.addOrUpdatePinnedCalendarUsers,
    {
      onSuccess: async (newPinnedUsers) => {
        enqueueSnackbar('Successfully saved users', { variant: 'success' });
        await queryClient.invalidateQueries(['list-pinned-calendar-users']);
        reset({ selectedUsers: newPinnedUsers });
      },
      onError: () => {
        enqueueSnackbar('Failed to save users', { variant: 'error' });
      },
    }
  );

  // const { data: allUsers } = useQuery(['list-users'], UserAPI.listUsers);
  const { data: allUsers } = useQuery(['calendar-users-list'], () =>
    getCalendarUserList()
  );

  // Role and Location based filtering logic
  const isAdminOrPM =
    user.role === 'Administrator' || user.role === 'ProjectManager';
  const isSupervisor = user.role === 'Supervisor';
  const isTechnician = user.role === 'Technician';
  const supervisorBranch = user.branch;
  const supervisorState = supervisorBranch?.state;

  // Extract unique states and branches from users
  const statesWithBranches = useMemo(() => {
    if (!allUsers) return [];

    const stateMap = new Map<string, Set<string>>();

    allUsers.forEach((u) => {
      if (u.branch?.state && u.branch?.name && !u.isLimitedUser) {
        if (!stateMap.has(u.branch.state)) {
          stateMap.set(u.branch.state, new Set());
        }
        stateMap.get(u.branch.state)!.add(u.branch.name);
      }
    });

    return Array.from(stateMap.entries())
      .map(([state, branches]) => ({
        state,
        branches: Array.from(branches).sort(),
      }))
      .sort((a, b) => a.state.localeCompare(b.state));
  }, [allUsers]);

  //Filter states and branches based on user role
  const filteredStatesWithBranches = useMemo(() => {
    if (isAdminOrPM) return statesWithBranches;
    if (isSupervisor && supervisorBranch) {
      // Only branches in supervisor's state
      return statesWithBranches.filter((s) => s.state === supervisorState);
    }
    // Supervisor without branch or org has no branches: show all
    if (isSupervisor) return statesWithBranches;
    // Technicians: no filter
    return [];
  }, [
    isAdminOrPM,
    isSupervisor,
    supervisorBranch,
    supervisorState,
    statesWithBranches,
  ]);

  // Determine if organization has branches
  const orgHasBranches = allBranchesData && allBranchesData.length > 0;

  // Determine if supervisor dropdown should be hidden
  const hideSupervisorDropdown =
    isSupervisor && orgHasBranches && !supervisorBranch;

  // Filter user roles based on current user's role
  const filteredUserRoles = useMemo(() => {
    if (isAdminOrPM) return USER_ROLES;
    if (isSupervisor)
      return USER_ROLES.filter((r) =>
        ['Supervisor', 'Technician'].includes(r.value)
      );
    return [];
  }, [isAdminOrPM, isSupervisor]);

  // Initialize state filters when data loads
  useEffect(() => {
    if (filteredStatesWithBranches.length > 0 && stateFilters.length === 0) {
      setStateFilters(
        filteredStatesWithBranches.map((s) => ({
          ...s,
          expanded: false,
        }))
      );
    }
  }, [filteredStatesWithBranches, stateFilters.length]);

  // Auto-update states based on selected branches
  useEffect(() => {
    const statesToAdd: string[] = [];
    const statesToRemove: string[] = [];

    filteredStatesWithBranches.forEach(({ state, branches }) => {
      const allBranchesSelected = branches.every((branch) =>
        selectedBranches.includes(branch)
      );

      if (allBranchesSelected && branches.length > 0) {
        if (!selectedStates.includes(state)) {
          statesToAdd.push(state);
        }
      } else {
        if (selectedStates.includes(state)) {
          statesToRemove.push(state);
        }
      }
    });

    if (statesToAdd.length > 0 || statesToRemove.length > 0) {
      setSelectedStates((prev) => {
        let updated = [...prev];
        updated = updated.filter((s) => !statesToRemove.includes(s));
        updated = [...updated, ...statesToAdd];
        return updated;
      });
    }
  }, [selectedBranches, filteredStatesWithBranches, selectedStates]);

  // Add useEffect to sync view state with props
  useEffect(() => {
    if (props.view !== view) {
      setView(props.view);
    }
  }, [props.view]);

  // Filter users based on selected states, branches, and roles
  const filteredUsers = useMemo(() => {
    if (!allUsers) return [];

    // Permission-based filtering
    let baseUsers: typeof allUsers = [];
    if (isAdminOrPM) {
      baseUsers = allUsers;
    } else if (isSupervisor && supervisorBranch) {
      baseUsers = allUsers.filter(
        (u) =>
          ['Supervisor', 'Technician'].includes(u.role) &&
          u.branch?.state === supervisorState
      );
    } else if (isSupervisor) {
      baseUsers = allUsers.filter((u) =>
        ['Supervisor', 'Technician'].includes(u.role)
      );
    } else if (isTechnician) {
      baseUsers = allUsers.filter(
        (u) => u.branch?.state === user.branch?.state
      );
    } else {
      baseUsers = [];
    }

    // Apply selected filters
    if (
      selectedStates.length === 0 &&
      selectedBranches.length === 0 &&
      selectedRoles.length === 0
    ) {
      return baseUsers;
    }

    return baseUsers.filter((u) => {
      let matchesLocation = true;
      let matchesRole = true;

      // Location filter (branches or states)
      if (selectedBranches.length > 0 || selectedStates.length > 0) {
        if (selectedBranches.length > 0) {
          matchesLocation = selectedBranches.includes(u.branch?.name);
        } else if (selectedStates.length > 0) {
          matchesLocation = selectedStates.includes(u.branch?.state);
        }
      }

      // Role filter
      if (selectedRoles.length > 0) {
        matchesRole = selectedRoles.includes(u.role);
      }

      return matchesLocation && matchesRole;
    });
  }, [
    allUsers,
    isAdminOrPM,
    isSupervisor,
    isTechnician,
    supervisorBranch,
    supervisorState,
    user.branch,
    selectedStates,
    selectedBranches,
    selectedRoles,
  ]);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
    setActiveTab(0);
  };

  const toggleState = (state: string) => {
    setStateFilters((prev) =>
      prev.map((sf) =>
        sf.state === state ? { ...sf, expanded: !sf.expanded } : sf
      )
    );
  };

  const handleStateCheck = (state: string, checked: boolean) => {
    const stateBranches =
      filteredStatesWithBranches.find((s) => s.state === state)?.branches || [];

    if (checked) {
      setSelectedBranches((prev) => {
        const newBranches = [...prev];
        stateBranches.forEach((branch) => {
          if (!newBranches.includes(branch)) {
            newBranches.push(branch);
          }
        });
        return newBranches;
      });
    } else {
      setSelectedBranches((prev) =>
        prev.filter((b) => !stateBranches.includes(b))
      );
    }
  };

  const handleBranchCheck = (branch: string, checked: boolean) => {
    if (checked) {
      setSelectedBranches((prev) => [...prev, branch]);
    } else {
      setSelectedBranches((prev) => prev.filter((b) => b !== branch));
    }
  };

  const handleRoleCheck = (role: Role, checked: boolean) => {
    if (checked) {
      setSelectedRoles((prev) => [...prev, role]);
    } else {
      setSelectedRoles((prev) => prev.filter((r) => r !== role));
    }
  };

  const isStateFullySelected = (state: string) => {
    const stateBranches =
      filteredStatesWithBranches.find((s) => s.state === state)?.branches || [];
    return (
      stateBranches.length > 0 &&
      stateBranches.every((branch) => selectedBranches.includes(branch))
    );
  };

  const isStatePartiallySelected = (state: string) => {
    const stateBranches =
      filteredStatesWithBranches.find((s) => s.state === state)?.branches || [];
    const selectedCount = stateBranches.filter((branch) =>
      selectedBranches.includes(branch)
    ).length;
    return selectedCount > 0 && selectedCount < stateBranches.length;
  };

  const isFilterOpen = Boolean(filterAnchorEl);

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    return selectedBranches.length + selectedRoles.length;
  }, [selectedBranches, selectedRoles]);

  const handleDateChange = (newDate: Date | null) => {
    if (newDate) {
      setSelectedDate(newDate);
      onNavigate('DATE', newDate);
    }
  };

  const getFormattedDateRange = () => {
    if (!selectedDate) return '';

    switch (view) {
      case 'month':
        return format(selectedDate, 'MMMM yyyy');

      case 'week': {
        const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
        const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 0 });
        return `${format(weekStart, 'dd MMM yyyy')} - ${format(
          weekEnd,
          'dd MMM yyyy'
        )}`;
      }

      case 'day':
        return format(selectedDate, 'dd MMMM yyyy');

      case 'agenda': {
        const agendaStart = selectedDate;
        const agendaEnd = addMonths(selectedDate, 1);
        return `${format(agendaStart, 'dd MMM yyyy')} - ${format(
          agendaEnd,
          'dd MMM yyyy'
        )}`;
      }

      default:
        return format(selectedDate, 'MMMM dd, yyyy');
    }
  };

  // Determine calendar view based on current calendar view
  const getCalendarView = (): ('year' | 'date' | 'month')[] => {
    if (view === 'month') {
      return ['year', 'month'];
    } else {
      return ['year', 'month', 'date'];
    }
  };

  // Determine openTo based on current calendar view
  const getOpenTo = () => {
    if (view === 'month') {
      return 'month' as const;
    } else {
      return 'date' as const;
    }
  };

  return (
    <div>
      <Toolbar variant="dense" disableGutters className={classes.topToolbar}>
        <Box className={classes.navigationContainer}>
          {!isMobile && (
            <Button
              variant="outlined"
              color="primary"
              className={classes.todayBtn}
              onClick={() => onNavigate('TODAY')}>
              TODAY
            </Button>
          )}
          <IconButton size="medium" onClick={() => onNavigate('PREV')}>
            <ChevronLeftIcon />
          </IconButton>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              variant="inline"
              format="MMMM dd, yyyy"
              value={selectedDate}
              onChange={handleDateChange}
              views={getCalendarView()}
              openTo={getOpenTo()}
              labelFunc={() => getFormattedDateRange()}
              InputProps={{
                disableUnderline: true,
                className: classes.datePickerInput,
                readOnly: true,
              }}
              InputAdornmentProps={{
                position: 'end',
                style: { marginLeft: 0 },
              }}
              PopoverProps={{
                PaperProps: {
                  className: classes.pickerPaper,
                },
              }}
            />
          </MuiPickersUtilsProvider>
          <IconButton size="medium" onClick={() => onNavigate('NEXT')}>
            <ChevronRightIcon />
          </IconButton>
          <FormControl className={classes.formControl}>
            <InputLabel htmlFor="select-calendar-view">View</InputLabel>
            <Select
              id="select-calendar-view"
              value={view}
              label="Select View"
              variant="standard"
              onChange={(event) => {
                const newView = event.target.value as View;
                setView(newView);
                onView(newView);
              }}>
              <MenuItem value={'month'}>Month</MenuItem>
              <MenuItem value={'week'}>Week</MenuItem>
              <MenuItem value={'day'}>Day</MenuItem>
              <MenuItem value={'agenda'}>Agenda</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {!hideSupervisorDropdown && (
          <AuthContainer
            restrictTo={['Administrator', 'ProjectManager', 'Supervisor']}
            noAccessComponent={() => null}>
            <Box className={classes.userSelectContainer}>
              <CalendarMultiUserAutocomplete
                control={control}
                name="selectedUsers"
                rules={{}}
                errors={{}}
                label="View individual/combined calendars"
                onChange={(users) => {
                  setSelectedUsers(users);
                }}
                selectedUsers={selectedUsers}
                avoidLimitedUsers={true}
                users={filteredUsers}
                selectedBranches={selectedBranches}
                onRemoveBranch={(branch) => handleBranchCheck(branch, false)}
                selectedRoles={selectedRoles}
                onRemoveRole={(role) => handleRoleCheck(role, false)}
              />

              <Badge
                badgeContent={activeFilterCount}
                className={`${classes.filterBadge} ${
                  selectedUsers && selectedUsers.length > 0
                    ? classes.filterBadgeWithUsers
                    : ''
                }`}
                invisible={activeFilterCount === 0}>
                <IconButton
                  size="small"
                  className={`${classes.filterIconButton} ${
                    selectedUsers && selectedUsers.length > 0
                      ? classes.filterIconButtonWithUsers
                      : ''
                  }`}
                  onClick={handleFilterClick}
                  disableRipple>
                  <FilterListIcon />
                </IconButton>
              </Badge>
            </Box>
            {isDirty && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  const userIds = selectedUsers.map((u) => u.id);
                  addOrUpdateMutation(userIds);
                }}>
                Save
              </Button>
            )}
          </AuthContainer>
        )}

        {/* Filter Popover */}
        <Popover
          open={isFilterOpen}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          className={
            orgHasBranches ? classes.popover : classes.singleTabPopover
          }>
          <Box
            className={
              orgHasBranches ? classes.popoverContent : classes.singleTabBox
            }>
            {orgHasBranches ? (
              <>
                <Tabs
                  value={activeTab}
                  onChange={(_, newValue) => setActiveTab(newValue)}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth">
                  <Tab label="Branches" />
                  <Tab label="User Roles" />
                </Tabs>

                {/* Branches Tab */}
                <TabPanel value={activeTab} index={0}>
                  {filteredStatesWithBranches.length === 0 ? (
                    <Typography color="textSecondary" align="center">
                      No users are assigned to branches
                    </Typography>
                  ) : (
                    <List dense>
                      {stateFilters.map((stateFilter) => (
                        <React.Fragment key={stateFilter.state}>
                          <ListItem
                            className={classes.stateHeader}
                            onClick={() => toggleState(stateFilter.state)}>
                            <Checkbox
                              edge="start"
                              className={classes.blueCheckbox}
                              checked={isStateFullySelected(stateFilter.state)}
                              indeterminate={isStatePartiallySelected(
                                stateFilter.state
                              )}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleStateCheck(
                                  stateFilter.state,
                                  e.target.checked
                                );
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <ListItemText
                              primary={stateFilter.state}
                              primaryTypographyProps={{
                                className: classes.stateText,
                              }}
                            />
                            <IconButton size="small">
                              {stateFilter.expanded ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                          </ListItem>
                          <Collapse
                            in={stateFilter.expanded}
                            timeout="auto"
                            unmountOnExit>
                            <List component="div" dense disablePadding>
                              {stateFilter.branches.map((branch) => (
                                <ListItem
                                  key={branch}
                                  className={classes.branchItem}>
                                  <Checkbox
                                    edge="start"
                                    className={classes.blueCheckbox}
                                    checked={selectedBranches.includes(branch)}
                                    onChange={(e) =>
                                      handleBranchCheck(
                                        branch,
                                        e.target.checked
                                      )
                                    }
                                  />
                                  <ListItemText primary={branch} />
                                </ListItem>
                              ))}
                            </List>
                          </Collapse>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </TabPanel>

                {/* User Roles Tab */}
                <TabPanel value={activeTab} index={1}>
                  <List dense>
                    {filteredUserRoles.map((role) => (
                      <ListItem key={role.value} className={classes.roleItem}>
                        <Checkbox
                          edge="start"
                          className={classes.blueCheckbox}
                          checked={selectedRoles.includes(role.value as Role)}
                          onChange={(e) =>
                            handleRoleCheck(
                              role.value as Role,
                              e.target.checked
                            )
                          }
                        />
                        <ListItemText primary={role.label} />
                      </ListItem>
                    ))}
                  </List>
                </TabPanel>
              </>
            ) : (
              <>
                <Tabs
                  value={activeTab}
                  textColor="primary"
                  variant="fullWidth"
                  centered
                  className={classes.noIndicator}>
                  <Tab label="User Roles" />
                </Tabs>
                <TabPanel value={activeTab} index={0}>
                  <List dense>
                    {filteredUserRoles.map((role) => (
                      <ListItem key={role.value} className={classes.roleItem}>
                        <Checkbox
                          edge="start"
                          className={classes.blueCheckbox}
                          checked={selectedRoles.includes(role.value as Role)}
                          onChange={(e) =>
                            handleRoleCheck(
                              role.value as Role,
                              e.target.checked
                            )
                          }
                        />
                        <ListItemText primary={role.label} />
                      </ListItem>
                    ))}
                  </List>
                </TabPanel>
              </>
            )}
          </Box>
        </Popover>
      </Toolbar>
    </div>
  );
};

export default CalendarToolbar;
